import {
  EventEmitter,
  getCurrentUserHook,
  getDefaultRowCount,
  getTableRowCount,
  saveColumnWidth,
  saveSortingPersonalization,
  saveTableRowCount,
  SkeletonTableLoading,
  useSuperListHook,
  withErrorBoundary,
  Generator
} from '@nile/components';
import gql from 'graphql-tag';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import SuperTable from '../SuperTable/SuperTable';

const DEFAULT_ROW_COUNT = 50;

const getSort = (sortData, secondarySort) => {
  // Check if secondarySort is present
  // If it's not present return sortData || null
  if (isEmpty(secondarySort)) {
    return sortData || null;
  }

  // Check if secondarySort && sortData is not empty
  // In this case return secondarySort
  if (secondarySort && isEmpty(sortData)) {
    return secondarySort;
  }

  // Now both secondary and sort is present
  if (secondarySort && sortData) {
    let updatedSort = cloneDeep(sortData);
    // check if sortData is Array else put it into array
    if (!Array.isArray(updatedSort)) {
      updatedSort = [updatedSort];
    }

    if (Array.isArray(secondarySort)) {
      // Check if secondary sort attribute is present in array
      // If secondarySort is not present push it to updatedSort
      secondarySort.forEach((sort) => {
        const isSecondarySortPresent = updatedSort.find((el) =>
          isEqual(el, sort)
        );
        if (!isSecondarySortPresent) {
          updatedSort.push(sort);
        }
      });
    } else {
      const isSecondarySortPresent = updatedSort.find((el) =>
        isEqual(el, secondarySort)
      );
      if (!isSecondarySortPresent) {
        updatedSort.push(secondarySort);
      }
    }
    return updatedSort;
  }
};

const constructCondition = ({ condition, formatOperands }) => {
  const operands = condition?.operands || Array.isArray(condition);
  let operandsData = operands?.length > 0 ? operands : null;
  if (operandsData) {
    return {
      joinOperator: condition?.joinOperator || 'AND',
      operands: formatOperands ? formatOperands(operandsData) : operandsData
    };
  }
  return null;
};

const getCondition = ({
  defaultCondition,
  tableCondition,
  advanceFilterCondition,
  formatOperands
}) => {
  const constructedDefaultConditons = constructCondition({
    condition: defaultCondition
  });
  const constructedTableConditions = constructCondition({
    condition: tableCondition,
    formatOperands: formatOperands
  });
  const constructedAdvancedFilterConditions = constructCondition({
    condition: advanceFilterCondition
  });

  if (
    !constructedDefaultConditons &&
    !constructedTableConditions &&
    !constructedAdvancedFilterConditions
  ) {
    return null;
  }

  const operands = [];
  if (constructedDefaultConditons) {
    operands.push(constructedDefaultConditons);
  }
  if (constructedTableConditions) {
    operands.push(constructedTableConditions);
  }
  if (constructedAdvancedFilterConditions) {
    operands.push(constructedAdvancedFilterConditions);
  }

  if (operands.length > 1) {
    return {
      joinOperator: 'AND',
      operands: operands
    };
  }

  return operands?.[0] || null;
};

const SuperList = (props) => {
  const {
    data = [],
    setData = () => {},
    onSuccessCallback,
    defaultCondition, // DefaultCondition
    tableCondition, // condition to be sent to table only
    advanceFilterCondition,
    client,
    query,
    listInfoVariables = {},
    appendViewIdToListInfo,
    viewId,
    tableName,
    records = [],
    setRecords = () => {},
    defaultSort,
    secondarySort,
    additionalVariables = {},
    fetchPolicy = 'network-only',
    accessorKey,
    dataKey,
    columns = [],
    dynamicRowCount,
    defaultColumnCells,
    noBulkSelect,
    checkBoxDisabledCheck,
    editCellId,
    idKey,
    maintainSelection,
    enableSelectAll,
    columnWidthPersonalization,
    getAttributeName,
    defaultRowCount,
    checkRowHighlight,
    checkDisabledRow,
    liveUpdateCondition,
    enableLiveUpdate,
    wsPayload,
    isGroupingEnabled,
    groupAccessor,
    defaultShowFilter,
    enableColumnPinning,
    headerBgColor,
    noHeaderBorder,
    tableInitialState,
    noOverscan,
    alignEmptyState,
    selectAllBannerStyles,
    sortingPersonalization,
    checkBoxSize,
    padded,
    alignStart,
    actions,
    emptyState,
    isClientPagination,
    disableSelectAll,
    isLoading,
    skeletonLoading,
    refreshEventEmitters,
    registerPageDelay,
    defaultExpanded,
    onLiveUpdateRefresh,
    showLiveUpdate,
    isLiveUpdateInProgress,
    onMessage,
    errorCallback,
    appendVariablesToInput, // To send variables inside input
    queryGenerator,
    refreshButtonStyles,
    useWatchQuery,
    containerClassName,
    getEntityAttributeName,
    formatOperands,
    overScanCount,
    appendListInfoToInput,
    headerSticky = false,
    stickyTop = 0
  } = props;

  if (isEmpty(columns) || skeletonLoading) {
    return (
      <div id="superlist-skeleton-loading">
        <SkeletonTableLoading />
      </div>
    );
  }

  // Logic in maintaining records
  // Whenever the record is changed we need to maintain 2 copies
  // 1. The records set by table while selecting
  // 2. The records set from outside. Eg. clearing the selection after API.

  // When the user select a record from the table onRecordChange function is called.
  // This will update the local state and the global state (records passed as props by the user)

  // The useEffect (Records useEffect) will check for similarity in records passed as props and the local state
  // If the records are not equal, it will update the local state to the updated state from records and call fetchData to refresh the table. This will reset the page to 1 but maintains the filters and sort

  const [currentRecords, setCurrentRecords] = useState(props?.records);

  const onRecordChange = (updatedRecords) => {
    setCurrentRecords(updatedRecords);
    if (setRecords) {
      setRecords(updatedRecords);
    }
  };

  const viewIdObj = appendViewIdToListInfo
    ? {
        view: {
          id: viewId
        }
      }
    : {};

  const isMountedRef = React.useRef(false);
  const subscriptionRef = React.useRef(null);

  const cleanupQuerySubscription = () => {
    if (subscriptionRef.current && useWatchQuery) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }
  };

  const user = getCurrentUserHook();
  const getRowCount = () => {
    if (defaultRowCount) {
      return defaultRowCount;
    }
    if (dynamicRowCount) {
      return (
        getTableRowCount(tableName) ||
        getDefaultRowCount(tableName) ||
        DEFAULT_ROW_COUNT
      );
    }
    return getDefaultRowCount(tableName) || DEFAULT_ROW_COUNT;
  };
  const ROW_COUNT = getRowCount();
  const DEFAULT_CONDITION = getCondition({ defaultCondition, tableCondition });
  const [rowCount, setRowCount] = useState(ROW_COUNT);
  const [loading, setLoading] = useState(true);
  const [showInlineFilter, setShowInlineFilter] = useState(
    !!tableCondition || defaultShowFilter
  );

  const { listInfo, setListInfo } = useSuperListHook({
    viewId,
    tableName
  });
  // Construct listInfo

  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', header: 'name', columns: columns }
  ]);

  const fetchData = async (pageOptions) => {
    try {
      setLoading(true);

      const sort = getSort(
        pageOptions?.sort || listInfo?.sort || null,
        secondarySort
      );

      const conditionData = getCondition({
        defaultCondition,
        tableCondition,
        advanceFilterCondition,
        formatOperands
      });

      const listInfoData = {
        pageSize: rowCount,
        page: pageOptions?.page || listInfo?.page || 1,
        sort: isGroupingEnabled ? (sort || [])?.reverse() : sort,
        condition: conditionData,
        ...viewIdObj,
        ...listInfoVariables
      };

      let variables = {
        listInfo: listInfoData,
        ...additionalVariables
      };

      if (appendVariablesToInput) {
        variables = {
          input: variables
        };
      }
      if (appendListInfoToInput) {
        variables = {
          input: listInfoData,
          ...additionalVariables
        };
      }

      let generator = null;
      let queryData = query;

      if (queryGenerator && !query) {
        if (
          !queryGenerator?.operation ||
          !queryGenerator?.getFields ||
          !queryGenerator?.getVariables
        ) {
          throw new Error('Invalid query generator in Superlist');
        }

        generator = new Generator({
          operation: queryGenerator.operation,
          variables: queryGenerator.getVariables({ variables }),
          fields: queryGenerator.getFields()
        });
        const queryString = generator.queryBuilder();
        queryData = gql`
          ${queryString.query}
        `;
      }

      if (useWatchQuery) {
        // Cleanup any previous query subscriptions
        cleanupQuerySubscription();
        const observable = client.watchQuery({
          query: queryData,
          variables,
          fetchPolicy: 'cache-and-network',
          notifyOnNetworkStatusChange: true
        });

        subscriptionRef.current = observable.subscribe({
          next: ({ data: listData, loading: queryLoading, networkStatus }) => {
            if (queryLoading && networkStatus === 1 && !listData) {
              setLoading(true);
              return;
            }

            if (onSuccessCallback) {
              onSuccessCallback({
                response: { data: listData },
                data: listData?.[accessorKey]
              });
            }

            if (listData?.[accessorKey]) {
              setData(listData[accessorKey][dataKey]);
              const listInfoSort = [
                ...(listData[accessorKey]?.listInfo?.sort || [])
              ];
              setListInfo({
                ...listData[accessorKey]?.listInfo,
                sort: isGroupingEnabled ? listInfoSort?.reverse() : listInfoSort
              });
              setLoading(false);
            }
          },
          error: (err) => {
            console.log(err, 'superlist watchQuery error');
            if (errorCallback) {
              errorCallback({ error: err });
            }
            setLoading(false);
          }
        });

        return subscriptionRef.current;
      }
      const response = await client.query({
        query: queryData,
        variables,
        fetchPolicy
      });

      if (onSuccessCallback) {
        onSuccessCallback({
          response: response,
          data: response?.data?.[accessorKey]
        });
      }
      if (response?.data?.[accessorKey]) {
        setData(response?.data?.[accessorKey]?.[dataKey]);
        const listInfoSort = [
          ...(response?.data?.[accessorKey]?.listInfo?.sort || [])
        ];
        setListInfo({
          ...response?.data?.[accessorKey]?.listInfo,
          sort: isGroupingEnabled ? listInfoSort?.reverse() : listInfoSort
        });
        setLoading(false);
      }
    } catch (err) {
      console.log(err, 'superlist error');
      if (errorCallback) {
        errorCallback({ error: err });
      }
    } finally {
      isMountedRef.current = true;
      if (!useWatchQuery) {
        // In case of useWatchQuery, loading state is managed by watchQuery
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // When table condition changes we'll fetchData
    // We'll reset records
    if (isMountedRef?.current) {
      fetchData({
        page: 1
      });
      onRecordChange(null);
    }
  }, [tableCondition, advanceFilterCondition]);

  useEffect(() => {
    if (isMountedRef?.current) {
      // When column changes we'll call fetchData
      // we'll setColumns
      // We'll reset records selected
      setColumnGroup([{ Header: 'Name', header: 'name', columns: columns }]);
      fetchData({
        page: 1
      });
    }
  }, [columns]);

  useEffect(() => {
    // This is the main useEffect that is called when the component mounts and viewId changes
    // While the viewId Changes we'll check if listInfo is present if it's present we'll call it else we'll use default conditions
    fetchData(
      listInfo || {
        page: 1,
        sort: defaultSort,
        conditions: DEFAULT_CONDITION
      }
    );
    // When the viewId mounts depending on condition we'll show filter
    // Added timeOut to prevent Race condition
    const showFilter = !!tableCondition || defaultShowFilter;
    setTimeout(() => {
      setShowInlineFilter(showFilter);
    }, 1);
    return function cleanup() {
      isMountedRef.current = false;
      // Unsubscribe from watchQuery if active
      cleanupQuerySubscription();
      // When the viewId changes we'll reset the records
      if (setRecords && !maintainSelection) {
        onRecordChange(null);
      }
      // When the viewId changes we'll close the filter
      setShowInlineFilter(false);
    };
  }, [viewId]);

  const resetRecordsOnTabChange = () => {
    if (setRecords && !maintainSelection) {
      onRecordChange(null);
    }
  };

  useEffect(() => {
    EventEmitter.subscribe('tabChange', resetRecordsOnTabChange);
    return () => {
      EventEmitter.unsubscribe('tabChange');
      cleanupQuerySubscription();
    };
  }, []);

  useEffect(() => {
    const refreshEventEmittersList =
      refreshEventEmitters && Array.isArray(refreshEventEmitters)
        ? refreshEventEmitters
        : [];

    refreshEventEmittersList.forEach((event) => {
      EventEmitter.subscribe(event, () => {
        fetchData({ page: 1 });
      });
    });

    return () => {
      refreshEventEmittersList.forEach((event) => {
        EventEmitter.unsubscribe(event);
      });
      // Unsubscribe from watchQuery if active
    };
  }, [refreshEventEmitters, fetchData]);

  useEffect(() => {
    // UseEffect to maintain records equality //
    const currentSelectedRecords = isEmpty(currentRecords)
      ? null
      : currentRecords;
    const recordsFromProps = isEmpty(records) ? null : records;
    const isRecordsEqual = isEqual(recordsFromProps, currentSelectedRecords);
    if (!isRecordsEqual) {
      setCurrentRecords(recordsFromProps);
      fetchData({
        page: 1
      });
      if (!maintainSelection) {
        EventEmitter.dispatch('refreshSelection', recordsFromProps);
      }
    }
  }, [records, currentRecords]);

  return (
    <div className="fhp w100">
      <SuperTable
        context={{
          records: currentRecords,
          setRecords: onRecordChange,
          viewConditions: tableCondition
        }}
        columns={columnGroup}
        data={data || []}
        refreshTable={(_, pageOptions) => {
          fetchData(pageOptions);
        }}
        viewId={viewId}
        listInfo={listInfo}
        isLoading={loading || isLoading}
        tableName={tableName}
        user={user}
        dynamicRowCount={dynamicRowCount}
        rowCount={rowCount}
        setRowCount={setRowCount}
        saveTableRowCount={saveTableRowCount}
        columnWidthPersonalization={columnWidthPersonalization}
        saveColumnWidth={saveColumnWidth}
        defaultColumnCells={defaultColumnCells}
        noBulkSelect={noBulkSelect}
        checkBoxDisabledCheck={checkBoxDisabledCheck}
        editCellId={editCellId}
        idKey={idKey}
        maintainSelection={maintainSelection}
        enableSelectAll={enableSelectAll}
        getAttributeName={getAttributeName}
        getEntityAttributeName={getEntityAttributeName}
        checkRowHighlight={checkRowHighlight}
        checkDisabledRow={checkDisabledRow}
        liveUpdateCondition={liveUpdateCondition}
        enableLiveUpdate={enableLiveUpdate}
        wsPayload={wsPayload}
        // END: For Live update supertable
        isGroupingEnabled={isGroupingEnabled}
        groupAccessor={groupAccessor}
        defaultShowFilter={defaultShowFilter}
        enableColumnPinning={enableColumnPinning}
        headerBgColor={headerBgColor}
        noHeaderBorder={noHeaderBorder}
        tableInitialState={tableInitialState}
        noOverscan={noOverscan}
        alignEmptyState={alignEmptyState}
        selectAllBannerStyles={selectAllBannerStyles}
        saveSortingPersonalization={saveSortingPersonalization}
        sortingPersonalization={sortingPersonalization}
        checkBoxSize={checkBoxSize}
        padded={padded}
        alignStart={alignStart}
        actions={actions}
        emptyState={emptyState}
        isClientPagination={isClientPagination}
        disableSelectAll={disableSelectAll}
        defaultExpanded={defaultExpanded}
        onLiveUpdateRefresh={onLiveUpdateRefresh}
        showLiveUpdate={showLiveUpdate}
        isLiveUpdateInProgress={isLiveUpdateInProgress}
        onMessage={onMessage}
        registerPageDelay={registerPageDelay}
        isSuperList
        showInlineFilter={showInlineFilter}
        refreshButtonStyles={refreshButtonStyles}
        containerClassName={containerClassName}
        overScanCount={overScanCount}
        getEntityAttributeName={getEntityAttributeName}
        headerSticky={headerSticky}
        stickyTop={stickyTop}
      />
    </div>
  );
};
export default withErrorBoundary(SuperList);
